import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, Sparkles, ArrowRight, HelpCircle } from "lucide-react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getGuide, GUIDES, GUIDE_CATEGORIES } from "@/data/guides";
import { SERVICES } from "@/data/site";

export default function GuidePage() {
  const { slug } = useParams();
  const g = getGuide(slug);
  if (!g) return <Navigate to="/404" replace />;
  const cat = GUIDE_CATEGORIES.find((c) => c.id === g.category)?.label;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: g.title,
    description: g.excerpt,
    datePublished: "2026-06-01",
    author: { "@type": "Organization", name: "Effizent Europe" },
    publisher: { "@type": "Organization", name: "Effizent Europe" },
    mainEntityOfPage: {
      "@type": "FAQPage",
      mainEntity: g.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  };

  return (
    <Layout>
      <Seo title={`${g.title} | Effizent Europe`} description={g.excerpt} jsonLd={jsonLd} />
      <PageHero eyebrow={`Guide · ${cat}`} title={g.title} subtitle={g.excerpt} crumbs={[{ label: "Learn", to: "/learn" }, { label: cat }]}>
        <p className="mt-6 inline-flex items-center gap-2 text-sm text-warm/50"><Clock className="h-4 w-4 text-gold" /> {g.readTime} read · Updated {g.updated}</p>
      </PageHero>

      <article className="max-w-3xl mx-auto px-6 md:px-8 py-16">
        {/* Quick answer — AI/GEO optimized */}
        <Reveal>
          <div data-testid="quick-answer" className="rounded-3xl bg-navy text-warm p-8 relative overflow-hidden">
            <div className="spotlight absolute inset-0 pointer-events-none" />
            <p className="accent-eyebrow text-gold flex items-center gap-2 relative"><Sparkles className="h-4 w-4" /> Quick answer</p>
            <p className="text-warm/85 text-lg leading-relaxed mt-4 relative">{g.quickAnswer}</p>
          </div>
        </Reveal>

        <div className="mt-14 space-y-12">
          {g.sections.map((sec, i) => (
            <Reveal key={i}>
              <h2 className="font-playfair text-3xl text-navy">{sec.h}</h2>
              <p className="text-navy/70 text-lg leading-relaxed mt-4">{sec.p}</p>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <h2 className="font-playfair text-3xl text-navy mt-16 mb-6 flex items-center gap-3"><HelpCircle className="h-7 w-7 text-gold" /> Frequently asked</h2>
          <Accordion type="single" collapsible>
            {g.faqs.map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} data-testid={`guide-faq-${i}`}>
                <AccordionTrigger className="text-left font-500 text-navy hover:text-gold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-navy/65 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="mt-4 text-sm text-navy/60">
            <a href="/faq.html" data-testid="guide-faq-full-link" className="text-maroon-accent font-600 link-underline">See our full FAQ</a>
          </p>
        </Reveal>

        {/* Special bridge link to the corresponding full static guide */}
        {g.bridge && (
          <Reveal>
            <p data-testid="guide-bridge-line" className="mt-14 rounded-2xl border border-gold/40 bg-gold/10 px-6 py-5 text-navy/80 text-lg">
              {g.bridge.pre}
              <a href={g.bridge.href} data-testid="guide-bridge-link" className="text-maroon-accent font-600 link-underline">{g.bridge.linkText}</a>{g.bridge.pre ? "." : ""}
            </p>
          </Reveal>
        )}

        {/* Secondary bridge link (e.g. visa comparison) */}
        {g.bridge2 && (
          <Reveal>
            <p data-testid="guide-bridge2-line" className="mt-4 rounded-2xl border border-gold/40 bg-gold/10 px-6 py-5 text-navy/80 text-lg">
              {g.bridge2.pre}
              <a href={g.bridge2.href} data-testid="guide-bridge2-link" className="text-maroon-accent font-600 link-underline">{g.bridge2.linkText}</a>.
            </p>
          </Reveal>
        )}

        {/* Related services (internal linking) */}
        {g.relatedServices?.length > 0 && (
          <Reveal>
            <div className="mt-16">
              <p className="accent-eyebrow text-terracotta mb-5">How we can help</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {g.relatedServices.map((r) => {
                  const s = SERVICES.find((x) => x.slug === r);
                  if (!s) return null;
                  return (
                    <Link key={r} to={`/services/${r}`} data-testid={`guide-service-${r}`} className="group flex items-center justify-between rounded-2xl border border-navy/10 bg-white px-5 py-4 hover:border-gold/50 transition-colors">
                      <span className="text-navy font-500">{s.title}</span>
                      <ArrowRight className="h-4 w-4 text-navy/30 group-hover:text-gold group-hover:translate-x-1 transition-all" />
                    </Link>
                  );
                })}
              </div>
            </div>
          </Reveal>
        )}

        {/* Related guides */}
        {g.related?.length > 0 && (
          <div className="mt-16 border-t border-navy/10 pt-10">
            <p className="accent-eyebrow text-terracotta mb-5">Keep reading</p>
            <div className="space-y-2">
              {g.related.map((r) => {
                const rg = GUIDES.find((x) => x.slug === r);
                if (!rg) return null;
                return (
                  <Link key={r} to={`/guides/${r}`} className="group flex items-center justify-between py-3">
                    <span className="text-navy/80 group-hover:text-gold transition-colors">{rg.title}</span>
                    <ArrowRight className="h-4 w-4 text-navy/30 group-hover:text-gold group-hover:translate-x-1 transition-all" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </article>
    </Layout>
  );
}
